import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { forkJoin, interval } from 'rxjs';
import { finalize } from 'rxjs/internal/operators/finalize';
import { ModalService } from 'src/app/modal';
import { ContentTooltip } from 'src/app/models/content-tooltip';
import { Utils } from 'src/app/services/utils/utils.service';
import { environment } from '../../environments/environment';
import { ContactUsComponent } from '../components/contact-us/contact-us.component';
import { ContentText } from '../models/content-text';
import { ContentDataService } from '../services/content/content-data.service';
import { SharedStateService } from '../services/shared-state/shared-state.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.' + environment.brand + '.html',
  styleUrls: ['./home.component.' + environment.brand + '.scss'],
  providers: [ContentDataService]
})
export class HomeComponent implements OnInit {
  @ViewChild('contactUs') contactUs: ContactUsComponent;

  // for fetching template
  get apiUrl(): string {
    return environment.apiUrl;
  }

  // brand name for template
  get brand(): string {
    return environment.brand;
  }

  // tab view options
  activeTabIndex = 0;
  get justBrowsing(): boolean {
    return this.sharedStateService.justBrowsing;
  }

  headerImageVisible = false;

  // dialog modal and progress (spinner) options
  dialogHeader = '';
  dialogMessage = '';
  loaderVisible = false;
  dialogVisible = false;
  displayUpdateNotice = false;

  // dynamic text loaded from api
  pageName = '';
  contentTexts: ContentText[] = [];
  contentTooltips: ContentTooltip[] = [];

  get headerText(): string {
    const text = this.contentTexts.find(x => x.pageName == this.pageName && x.contentTextKey == 'Heading');
    return text != undefined ? text.contentTextValue : 'Heading';
  }

  get subHeaderText(): string {
    const text = this.contentTexts.find(x => x.pageName == this.pageName && x.contentTextKey == 'SubHeading');
    return text != undefined ? text.contentTextValue : 'SubHeading';
  }

  constructor(private sharedStateService: SharedStateService,
    protected contentDataService: ContentDataService,
    private router: Router,
    private utils: Utils,
    private swUpdate: SwUpdate,
    private modalService: ModalService) {
    this.utils.log('HomeComponent.constructor');
    // subscribe to route navigation
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.onNavigationEnd(event);
        if (this.swUpdate.isEnabled) {
          this.swUpdate.checkForUpdate();
        }
      }
    });

    // subscribe to shared state service events
    this.sharedStateService.onToggleLoader.subscribe(isVisible => {
      this.loaderVisible = isVisible;
    });

    this.sharedStateService.onShowDialog.subscribe(options => {
      this.dialogHeader = options.header;
      this.dialogMessage = options.message;
      this.dialogVisible = options.isVisible;
    });

    this.sharedStateService.onShowStart.subscribe(() => {
      this.onShowStart();
    });

    this.sharedStateService.onShowEnterCustomerDetails.subscribe(() => {
      this.onShowEnterCustomerDetails();
    });

    this.sharedStateService.onShowCustomerDetails.subscribe(() => {
      this.onShowCustomerDetails();
    });

    this.sharedStateService.onShowSelectPlan.subscribe(() => {
      this.onShowSelectPlan();
    });

    this.sharedStateService.onShowViewQuote.subscribe(() => {
      this.onViewQuote();
    });

    this.sharedStateService.onShowJustBrowsingVehicleDetails.subscribe(() => {
      this.router.navigate(['just-browsing/vehicle-details']);
    });
  }

  private onNavigationEnd(event: NavigationEnd) {
    //this.utils.log('routed to: ' + event.url);
    this.sharedStateService.justBrowsing = false;
    this.headerImageVisible = false;

    if (environment.brand == 'vwsa') {
      document.getElementById('appBody').style.marginTop = "100px";
    }

    // update ui
    window.scroll(0, 0);

    if (event.url == '/') {
      this.activeTabIndex = 0;
      this.pageName = 'Start';
      this.headerImageVisible = true;
      if (environment.brand == 'vwsa') {
        document.getElementById('appBody').style.marginTop = "0px";
      }
      this.sharedStateService.clearState();
    } else if (event.url.startsWith('/enter-details')) {
      this.activeTabIndex = 0;
      this.pageName = 'Existing Customer - Authentication';
    } else if (event.url.startsWith('/show-details')) {
      this.activeTabIndex = 0;
      this.pageName = 'Existing Customer - Details';
    } else if (event.url.startsWith('/select-plan')) {
      this.activeTabIndex = 1;
      this.pageName = 'Existing Customer - Select Plan';
    } else if (event.url.startsWith('/view-quote')) {
      this.activeTabIndex = 2;
      this.pageName = 'Existing Customer - View Quote';
    } else if (event.url.startsWith('/payment')) {
      this.activeTabIndex = 2;
      this.pageName = 'Payment Result';
    } else if (event.url.startsWith('/just-browsing/vehicle-details')) {
      this.activeTabIndex = 0;
      this.sharedStateService.justBrowsing = true;
      this.pageName = 'Just Browsing - Vehicle Details';
    } else if (event.url.startsWith('/just-browsing/select-plan')) {
      this.activeTabIndex = 1;
      this.sharedStateService.justBrowsing = true;
      this.pageName = 'Just Browsing - Select Plan';
    }
  }

  ngOnInit(): void {
    this.getPageContent();

    if (this.swUpdate.isEnabled) {
      // check for available app updates every x seconds
      interval(60 * 1000).subscribe(() => this.swUpdate.checkForUpdate());
      // this will fire if an update is available
      this.swUpdate.available.subscribe(() => {
        this.displayUpdateNotice = true;
      });
    }
  }

  onConfirmUpdate(result: boolean): void {
    this.displayUpdateNotice = false;

    if (result) {
      window.location.reload();
    }
  }

  getPageContent(): void {
    // loading dynamic text and tooltips    
    const getContentTexts = this.contentDataService.getContentText('');
    const getContentTooltips = this.contentDataService.getContentTooltips('');

    this.loaderVisible = true;
    forkJoin([getContentTexts, getContentTooltips])
      .pipe(finalize(() => {
        this.loaderVisible = false;
      }))
      .subscribe(results => {
        this.contentTexts = results[0];
        this.sharedStateService.contentTexts = this.contentTexts;
        //this.utils.log('Texts', JSON.stringify(this.contentTexts));
        this.contentTooltips = results[1];
        this.sharedStateService.contentTooltips = this.contentTooltips;
        //this.utils.log('Tooltips', JSON.stringify(this.contentTooltips));
      }, error => {
        this.utils.log('getPageContent', error);
      });
  }

  onShowStart(): void {
    this.router.navigate(['']);
  }

  onShowEnterCustomerDetails(): void {
    this.router.navigate(['enter-details']);
  }

  onShowCustomerDetails(): void {
    this.router.navigate(['show-details']);
  }

  onShowSelectPlan(): void {
    if (!this.sharedStateService.justBrowsing) {
      this.router.navigate(['select-plan']);
    } else {
      this.router.navigate(['just-browsing/select-plan']);
    }
  }

  onViewQuote(): void {
    this.router.navigate(['view-quote']);
  }

  onCloseDialog(): void {
    this.dialogVisible = false; // just in case
    if (this.sharedStateService.enterCustomerDetailsErrorCount >= 3) {
      this.contactUs.showDialog('');
    }
  }

  onShowContactUsDialog(): void {
    this.contactUs.showDialog('');
  }

  openModal(id: string): void {
    this.modalService.open(id);
  }

  closeModal(id: string): void {
    this.modalService.close(id);
  }
}
